import React, { useState, useCallback, useEffect, useRef } from 'react'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './TacticIntegration/TacticIntegration.module.scss'
import { AVAILABLE_BLOG_PLATFORMS, AVAILABLE_EMAIL_PLATFORMS } from './utils'
import { ChevronDown2 } from 'blaze-ui/icons'
import { LinkExternal } from 'blaze-ui/icons/20/LinkExternal'
import { useSwitchPlatform } from './hooks/useSwitchPlatform'
import { useInitiateOauthTrigger } from 'hooks/useInitiateOauthTrigger/useInitiateOauthTrigger'
import { useModals } from 'almanac-ui/Modal'
import { SwitchToDifferentPlatformModal } from './SwitchToDifferentPlatformModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface SwitchPlatformMenuProps {
  integration?: ExternalIntegration
  tactic: 'seo' | 'email'
}

export const SwitchPlatformMenu: React.FC<SwitchPlatformMenuProps> = ({ integration, tactic }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<ExternalIntegrationEnum | null>(null)
  const shouldTriggerRef = useRef(false)
  const { connect } = useSwitchPlatform({ oldIntegration: integration, tactic })
  const { openModal } = useModals()
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')

  const { handleConnect } = useInitiateOauthTrigger({
    provider: selectedProvider || ExternalIntegrationEnum.WordPress,
    popUp: true,
    onInitiatedOauth: connect,
  })

  // Trigger handleConnect when selectedProvider changes and we should trigger
  useEffect(() => {
    if (shouldTriggerRef.current && selectedProvider) {
      shouldTriggerRef.current = false
      handleConnect()
    }
  }, [selectedProvider, handleConnect])

  const handleAction = useCallback(
    async (key: React.Key) => {
      setIsMenuOpen(false)

      if (key === 'other-platform') {
        openModal(SwitchToDifferentPlatformModal, {
          integration,
          tactic,
        })
        return
      }

      const provider = key as ExternalIntegrationEnum
      setSelectedProvider(provider)
      shouldTriggerRef.current = true
    },
    [integration, openModal, tactic],
  )

  const availablePlatforms = tactic === 'seo' ? AVAILABLE_BLOG_PLATFORMS : AVAILABLE_EMAIL_PLATFORMS
  const otherPlatforms = availablePlatforms.filter((platform) => {
    if (platform.integrationType === ExternalIntegrationEnum.Wix && !wixEnabled) {
      return false
    }
    if (platform.integrationType === ExternalIntegrationEnum.Shopify && !shopifyEnabled) {
      return false
    }
    return platform.integrationType !== integration?.provider
  })

  return (
    <MenuTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} popoverClassName={Styles.popover}>
      <Button variant="tertiary" size="md" className={Styles.switchPlatformButton} endIcon={ChevronDown2}>
        Switch to Another Platform
      </Button>
      <Menu onAction={handleAction} aria-label="Switch Platform">
        {otherPlatforms.map((platform) => {
          const IconComponent = platform.icon as React.ComponentType<{ size?: number }>
          return (
            <Item key={platform.integrationType} textValue={platform.name}>
              <IconComponent size={20} />
              <Text variant="smallList">{platform.name}</Text>
            </Item>
          )
        })}
        {integration && (
          <Item key="other-platform" textValue="I'm using another platform">
            <LinkExternal size={20} />
            <Text variant="smallList">I'm using another platform</Text>
          </Item>
        )}
      </Menu>
    </MenuTrigger>
  )
}
